import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Agent CLI — agents are first-class headless citizens. The Auditor, Architect
// and Librarian are background workers that run automatically (the Auditor on
// PRs); this command inspects their runs and tunes their config. Manual run
// triggering was retired — it spent inference for no gain over the auto-runs.

const KNOWN_AGENT_FLAGS: ReadonlySet<string> = new Set([
  'limit', 'show', 'set', 'days', 'json', 'help',
])

export function assertKnownAgentFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_AGENT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_AGENT_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless agent':\n` +
      lines.join('\n') +
      `\nRun 'driftless agent --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so AGENT_HELP still resolves from './agent'.
export { AGENT_HELP } from './help-text'
import { AGENT_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(AGENT_HELP)
}

// Parse repeated --set key=value pairs into a config patch body. parseArgs only
// keeps the LAST value of a repeated flag, so scan argv directly (mirroring
// collectMulti) and split each on the first '='.
function collectSetPairs(args: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (let i = 0; i < args.length - 1; i++) {
    if (args[i] !== '--set') continue
    const pair = args[i + 1]
    if (pair === undefined || pair.startsWith('--')) continue
    const eq = pair.indexOf('=')
    if (eq <= 0) continue
    const key = pair.slice(0, eq).trim()
    const raw = pair.slice(eq + 1).trim()
    if (!key) continue
    // Coerce booleans for toggle keys; leave everything else as a string.
    if (raw === 'true') out[key] = true
    else if (raw === 'false') out[key] = false
    else out[key] = raw
  }
  return out
}

export async function agentCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownAgentFlags(flags)

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}/agent-runs`

  try {
    switch (sub) {
      case 'runs': {
        const params: string[] = []
        if (flags['limit']) params.push(`limit=${encodeURIComponent(String(flags['limit']))}`)
        const q = params.length ? `?${params.join('&')}` : ''
        const result = (await api.get(`${base}${q}`)) as any[]
        if (isJSON) { emitJSON(result); break }
        if (!Array.isArray(result) || !result.length) { console.log('No agent runs yet.'); break }
        for (const r of result) {
          const kind = (r.agent || r.kind || r.type || '?').toString().padEnd(10)
          const status = (r.status || '?').toString().padEnd(10)
          console.log(`${r.id}  ${kind} ${status} ${r.created_at ?? ''}`)
        }
        break
      }
      case 'run': {
        const id = positional[1]
        if (!id) { console.error('Error: a run id is required. Usage: driftless agent run <id>'); process.exit(1) }
        const result = (await api.get(`${base}/${encodeURIComponent(id)}`)) as any
        if (isJSON) { emitJSON(result); break }
        const run = result?.run ?? result
        console.log(`${run.id}  [${run.status ?? '?'}]  ${run.agent ?? run.kind ?? ''}`)
        if (run.created_at) console.log(`  created: ${run.created_at}`)
        if (run.summary) console.log(`  ${run.summary}`)
        const trace = result?.trace ?? run?.trace ?? []
        if (Array.isArray(trace) && trace.length) {
          console.log(`\n  trace (${trace.length})`)
          for (const t of trace) {
            const step = (t.step ?? t.type ?? t.event ?? '').toString()
            const detail = t.message ?? t.detail ?? t.content ?? ''
            console.log(`    ${step}${step && detail ? '  ' : ''}${detail}`)
          }
        }
        break
      }
      case 'config': {
        const setArgs = args.includes('--set') ? collectSetPairs(args) : {}
        const isSet = Object.keys(setArgs).length > 0
        if (isSet) {
          const result = (await api.patch(`${base}/config`, setArgs)) as any
          analyticsEvent('agent_config_set', ws, { keys: Object.keys(setArgs).join(',') })
          if (isJSON) { emitJSON(redactConfig(result)); break }
          console.log(`Agent config updated — ${Object.keys(setArgs).join(', ')}`)
          break
        }
        // --show (or no flag) → read. NEVER echo a raw key.
        const result = (await api.get(`${base}/config`)) as any
        if (isJSON) { emitJSON(redactConfig(result)); break }
        const cfg = redactConfig(result)
        for (const [k, v] of Object.entries(cfg)) {
          console.log(`  ${k.padEnd(22)} ${v}`)
        }
        break
      }
      case 'usage': {
        const params: string[] = []
        if (flags['days']) params.push(`days=${encodeURIComponent(String(flags['days']))}`)
        const q = params.length ? `?${params.join('&')}` : ''
        const result = (await api.get(`${base}/usage${q}`)) as any
        if (isJSON) { emitJSON(result); break }
        const rows = Array.isArray(result) ? result : (result?.usage ?? [])
        if (!rows.length) { console.log('No usage recorded for this window.'); break }
        for (const u of rows) {
          const label = u.agent ?? u.model ?? u.day ?? '?'
          const tokens = u.tokens ?? u.total_tokens ?? ''
          const cost = u.cost ?? u.cost_usd ?? ''
          console.log(`  ${String(label).padEnd(16)} ${tokens ? `${tokens} tok` : ''}${cost ? `  $${cost}` : ''}`)
        }
        break
      }
      case 'stats': {
        const result = (await api.get(`${base}/proposal-stats`)) as any
        if (isJSON) { emitJSON(result); break }
        const entries = Object.entries(result ?? {})
        if (!entries.length) { console.log('No proposal stats yet.'); break }
        for (const [k, v] of entries) console.log(`  ${k.padEnd(22)} ${JSON.stringify(v)}`)
        break
      }
      case 'models': {
        const result = (await api.get(`${base}/models`)) as any
        if (isJSON) { emitJSON(result); break }
        const list = Array.isArray(result) ? result : (result?.models ?? [])
        if (!list.length) { console.log('No models available.'); break }
        for (const m of list) {
          if (typeof m === 'string') console.log(`  ${m}`)
          else console.log(`  ${m.id ?? m.name ?? JSON.stringify(m)}${m.description ? `  — ${m.description}` : ''}`)
        }
        break
      }
      default:
        console.error(`Unknown agent subcommand: '${sub}'. Run 'driftless agent --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}

// Defensively strip any secret-bearing field so a raw BYOM key is NEVER echoed,
// even if the API returns it. Replace with a presence marker.
export function redactConfig(cfg: any): Record<string, unknown> {
  if (!cfg || typeof cfg !== 'object') return {}
  const SECRET_KEYS = ['opencode_key', 'api_key', 'key', 'secret', 'token']
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(cfg)) {
    const lower = k.toLowerCase()
    if (SECRET_KEYS.some((s) => lower === s || lower.endsWith(`_${s}`))) {
      out[k] = v ? '(set)' : '(not set)'
    } else {
      out[k] = v
    }
  }
  return out
}
